import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'
import { useForm } from '@domain-group/react-hooks'

import useProviderPriceList from '@hooks/useProviderPriceList'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'ForkProviderPriceListModal'

type ModalCallbacks = {
  closeModal: () => void,
  forkProviderPriceList: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  agencyId?: number,
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const defaultFormState = {
  agency_id: undefined,
  hide_items: false,
  title: '',
}

const ForkProviderPriceListModal = (props: ModalProps) => {
  const { agencyId, callbacks, showModal } = props
  const { closeModal, forkProviderPriceList } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const providerPriceList = digObject(modalPayload, 'providerPriceList', {})

  const {
    callbacks: {
      forkProviderPriceList: forkFn,
    },
    creating,
    updating,
  } = useProviderPriceList(providerPriceList)

  const formPayload = useForm(
    defaultFormState,
    {
      entity: { agency_id: agencyId },
      requiredFields: [{ key: 'title' }],
      validateOn: 'change',
    },
    [agencyId],
  )

  const {
    entityState,
    entityState: {
      agency_id,
      hide_items,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = formPayload

  const forkProviderPriceListPayload = {
    callbacks: {
      action: forkFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Price List was Forked',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Fork" titleSecondLine="Price List" />

      <SidebarModal.Content>
        <Form>
          {/*
           // @ts-expect-error TS2739 */}
          <EntitySelector
            disabled
            entityKey="providerPriceLists"
            label="* Master Price List"
            selectedItemId={providerPriceList.id}
          />

          {!!agencyId && (
            // @ts-expect-error TS2739
            <EntitySelector
              disabled
              entityKey="agencies"
              label="* Agency"
              marginTop="large"
              selectedItemId={agency_id}
            />
          )}

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Hide Price List Items" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="hide_items"
              value={hide_items}
              {...handlers}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => forkProviderPriceList(forkProviderPriceListPayload)}
          size="large"
        >
          {saveEnabled ? 'Fork Price List' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ForkProviderPriceListModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
